/**
 * Converts route children config to the parent/substep progress bar structure.
 *
 * @param {Array} routeChildren - Array of Vue router child route objects.
 * @param {Object} options - Configuration options for filtering and completion logic.
 * @param {string} [options.currentRouteName] - Name of the current route to mark as active.
 * @param {Array} [options.completedStepNames] - List of route names that are marked as completed.
 * @param {Function} [options.completeFn] - Custom function to determine if a route is completed.
 * @param {Function} [options.filterFn] - Custom function to filter routes for visibility.
 * @returns {Array} - Array of parent sections with their substeps.
 *
 * Example output:
 * [
 *   {
 *     label: 'Personal Information',
 *     id: 'personal-info',
 *     substeps: [
 *       { id: 'step1', name: 'Step 1', path: '/step1', label: 'Step 1', completed: true },
 *       { id: 'step2', name: 'Step 2', path: '/step2', label: 'Step 2', completed: false },
 *     ],
 *   },
 * ]
 */
export function buildParentProgressBarStructure(routeChildren, options = {}) {
  const {
    currentRouteName,
    completedStepNames = [],
    completeFn, // function(route, idx, array) => true/false
    filterFn, // (route) => boolean
  } = options;

  // Step 1: Filter relevant children
  const filteredRoutes = (routeChildren || []).filter(route => {
    if (route.meta && route.meta.showInProgressBar === false) return false;
    if (typeof filterFn === 'function') return filterFn(route);
    return true; // Default: include all routes not explicitly disabled
  });

  // Step 2: Group routes by parent section
  const groupedRoutes = {};
  filteredRoutes.forEach((route, index) => {
    const parentLabel = route.meta?.parentSection || 'general';
    if (!groupedRoutes[parentLabel]) {
      groupedRoutes[parentLabel] = [];
    }

    // Clone the route and assign a fallback order if not defined
    const clonedRoute = { ...route, meta: { ...route.meta } };
    clonedRoute.meta.order = clonedRoute.meta.order ?? index;
    groupedRoutes[parentLabel].push(clonedRoute);
  });

  // Step 3: Build parent sections with substeps
  const result = Object.entries(groupedRoutes).map(([parentLabel, routes]) => {
    const sortedRoutes = routes.slice().sort((a, b) => {
      return a.meta.order - b.meta.order;
    });

    return {
      label: parentLabel,
      id: parentLabel,
      substeps: sortedRoutes.map(route => {
        const isCompleted = completeFn
          ? completeFn(route)
          : completedStepNames.includes(route.name) || route.name === currentRouteName;

        return {
          id: route.name,
          name: route.name,
          path: route.path,
          label: route.meta?.progressLabel || route.name,
          completed: isCompleted,
        };
      }),
    };
  });

  // Step 4: Sort parent sections by the order of their first substep
  result.sort((a, b) => {
    const orderA = a.substeps[0]?.meta?.order ?? 0;
    const orderB = b.substeps[0]?.meta?.order ?? 0;
    return orderA - orderB;
  });

  return result.filter(parent => parent.substeps.length > 0);
}
